
import { useCallback } from 'react';
import { useAuth } from '@/contexts/auth';
import { sendNotification } from '@/utils/sendNotification';
import { EmailTemplateProps, EmailTemplateType } from '@/utils/email-templates/types';
import { fetchRecipientProfile } from '@/utils/profiles/fetchRecipientProfile';
import type { Poll } from '@/types/poll';
import type { InsightCard } from '@/types/insight';

export interface NotificationDispatcherOptions {
  countryCode?: string;
  constituencyCode?: string;
  pollData?: {
    title: string;
    description: string;
    results?: {
      totalVotes: number;
      topOption: string;
      agreePercentage?: number;
    };
  };
  insightData?: InsightCard;
  changes?: string[];
  recipientName?: string;
  notes?: string;
  credentials?: {
    email: string;
    password?: string;
  };
}

export const useNotificationDispatcher = () => {
  // Get profile from auth context if available, otherwise null
  let profile = null;
  try {
    // Try to get auth context, but handle case when it's not available
    const auth = useAuth();
    profile = auth.profile;
  } catch (e) {
    // Auth context not available, which is fine in some cases
    console.log('Auth context not available in useNotificationDispatcher');
  }

  const dispatch = useCallback(async (
    type: EmailTemplateType | string,
    recipients: string | string[],
    options: NotificationDispatcherOptions = {}
  ) => {
    try {
      const recipientsArray = Array.isArray(recipients) ? recipients : [recipients];
      
      // Handle empty recipient lists gracefully
      if (recipientsArray.length === 0) {
        console.log(`No recipients provided for ${String(type)} notification, skipping`);
        return true;
      }
      
      console.log(`Attempting to send ${String(type)} notification to ${recipientsArray.join(', ')}`, options);
      
      // Process each recipient individually to personalize messages
      for (const recipient of recipientsArray) {
        // Determine recipient name - use provided name or fetch from profile
        let recipientName = options.recipientName;
        
        if (!recipientName) {
          try {
            const { recipientName: fetchedName } = await fetchRecipientProfile(recipient);
            recipientName = fetchedName;
          } catch (error) {
            console.warn('Could not fetch recipient profile, using fallback name');
            recipientName = 'User'; // Fallback if profile fetch fails
          }
        }
        
        // Create a properly structured EmailTemplateProps object for this recipient
        const templateData: EmailTemplateProps = {
          recipientName: recipientName || 'User',
          pollData: options.pollData,
          insightData: options.insightData && {
            ...options.insightData,
            changes: options.changes
          },
          notes: options.notes,
          credentials: options.credentials
        };

        console.log('Prepared template data for recipient:', recipient, JSON.stringify(templateData, null, 2));
        
        // Normalize the type to handle various format patterns and ensure it's a valid EmailTemplateType
        let normalizedType: EmailTemplateType;
        
        // Map all document verification types correctly
        if (String(type) === 'documentVerified' || String(type) === 'document_verified') {
          normalizedType = 'documentVerified';
        } else if (String(type) === 'documentRejected' || String(type) === 'document_rejected') {
          normalizedType = 'documentRejected';
        } else if (String(type) === 'pollClosed' || String(type) === 'poll_closed') {
          normalizedType = 'pollClosed';
        } else if (String(type) === 'newPollPublished' || String(type) === 'new_poll_published') {
          normalizedType = 'newPollPublished';
        } else if (String(type) === 'pollsterStatusUpdated' || String(type) === 'pollster_status_updated') {
          normalizedType = 'pollsterStatusUpdated';
        } else if (String(type) === 'authLogin' || String(type) === 'auth_login') {
          normalizedType = 'authLogin';
        } else if (String(type) === 'authSignup' || String(type) === 'auth_signup' || String(type) === 'welcome') {
          normalizedType = 'authSignup';
        } else if (String(type) === 'accountDeletionPending' || String(type) === 'account_deletion_pending') {
          normalizedType = 'accountDeletionPending';
        } else if (String(type) === 'accountDeletionConfirmed' || String(type) === 'account_deletion_confirmed') {
          normalizedType = 'accountDeletionConfirmed';
        } else if (String(type) === 'accountDeletionCompleted' || String(type) === 'account_deletion_completed') {
          normalizedType = 'accountDeletionCompleted';
        } else {
          // Use the type as-is if it's already a valid EmailTemplateType
          normalizedType = String(type) as EmailTemplateType;
        }
        
        console.log(`Normalized notification type from "${type}" to "${normalizedType}"`);
        
        // Send to each recipient individually for personalization
        const result = await sendNotification(normalizedType, recipient, templateData);

        if (!result) {
          console.error(`Failed to send notification to ${recipient}`);
        } else {
          console.log(`Successfully sent ${normalizedType} notification to ${recipient}`);
        }
      }
      
      return true;
    } catch (error) {
      console.error('Notification dispatch error:', error);
      return false;
    }
  }, [profile]);

  return { dispatch };
};
